import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MainHeaderComponent } from '../../shared/main-header/main-header.component';
import { DataTableComponent } from '../../shared/data-table/data-table.component';
import { DatasetSchema, SchemasService } from '../../../services/schemas.service';

type AdminSchemaRow = DatasetSchema & {
  created_at_label?: string;
};

@Component({
  selector: 'app-list-admin-schemas',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatSnackBarModule,
    MainHeaderComponent,
    DataTableComponent
  ],
  templateUrl: './list-admin-schemas.component.html',
  styleUrl: './list-admin-schemas.component.scss'
})
export class ListAdminSchemasComponent implements OnInit {
  schemas: AdminSchemaRow[] = [];
  selectedItems: AdminSchemaRow[] = [];
  isLoading = true;

  columns: any[] = [
    { field: 'id', header: 'ID', filter: true, orderby: true },
    { field: 'name', header: 'Nombre', filter: true, orderby: true },
    { field: 'created_at_label', header: 'Fecha de creación', filter: true, orderby: false },
    { field: 'description', header: 'Descripción', filter: true, orderby: false }
  ];

  constructor(
    private schemasService: SchemasService,
    private router: Router,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.loadSchemas();
  }

  createSchema(): void {
    this.router.navigate(['/schemas/create']);
  }

  onEditSchema(item: AdminSchemaRow): void {
    this.router.navigate(['/schemas', item.id, 'edit']);
  }

  onSelectionChange(event: AdminSchemaRow[]): void {
    this.selectedItems = event ?? [];
  }

  onDeleteSchema(item: AdminSchemaRow): void {
    if (!confirm(`¿Eliminar schema "${item.name}"?`)) return;

    this.schemasService.deleteSchema$(item.id).subscribe({
      next: () => {
        this.snackBar.open('Schema eliminado correctamente.', 'OK', { duration: 3000 });
        this.loadSchemas();
      },
      error: () => {
        this.snackBar.open('No se pudo eliminar el schema.', 'Cerrar', { duration: 4000 });
      }
    });
  }

  onDeleteFromTable(event: any): void {
    if (!event) return;
    if (Array.isArray(event)) {
      if (!event.length) return;
      this.onDeleteSchema(event[0] as AdminSchemaRow);
      return;
    }
    this.onDeleteSchema(event as AdminSchemaRow);
  }

  deleteSelectedSchemas(): void {
    if (!this.selectedItems.length) return;
    if (!confirm('¿Eliminar los schemas seleccionados?')) return;

    const pending = [...this.selectedItems];
    let remaining = pending.length;

    pending.forEach((item) => {
      this.schemasService.deleteSchema$(item.id).subscribe({
        next: () => {
          remaining -= 1;
          if (remaining === 0) {
            this.selectedItems = [];
            this.snackBar.open('Schemas eliminados correctamente.', 'OK', { duration: 3000 });
            this.loadSchemas();
          }
        },
        error: () => {
          this.snackBar.open('No se pudieron eliminar todos los schemas.', 'Cerrar', {
            duration: 4000
          });
        }
      });
    });
  }

  private loadSchemas(): void {
    this.isLoading = true;
    this.schemasService.getAllSchemas$().subscribe({
      next: (schemas) => {
        this.schemas = (schemas ?? []).map((schema) => ({
          ...schema,
          created_at_label: this.formatCreatedAt(schema.created_at)
        }));
        this.isLoading = false;
      },
      error: () => {
        this.schemas = [];
        this.isLoading = false;
        this.snackBar.open('No se pudieron cargar los schemas administrables.', 'Cerrar', {
          duration: 4000
        });
      }
    });
  }

  private formatCreatedAt(value: unknown): string {
    const timestamp = this.dateValue(value);
    if (!timestamp) return 'Sin información';

    return new Date(timestamp).toLocaleDateString('es-CO', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  private dateValue(value: unknown): number {
    if (!value) return 0;
    if (typeof (value as any)?.toMillis === 'function') return (value as any).toMillis();
    if (typeof (value as any)?.seconds === 'number') return (value as any).seconds * 1000;
    if (value instanceof Date) return value.getTime();
    if (typeof value === 'string' || typeof value === 'number') {
      const timestamp = new Date(value).getTime();
      return Number.isNaN(timestamp) ? 0 : timestamp;
    }
    return 0;
  }
}
